import React, { useState, useEffect } from 'react';
import { Search, Mail, Calendar, Clock } from 'lucide-react';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { adminSubscribersAPI } from '../../utils/api';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../ui/toaster';

const SubscribersManagement = () => {
  const [subscribers, setSubscribers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeOnly, setActiveOnly] = useState(false);
  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 50,
    total: 0,
  });

  useEffect(() => {
    loadSubscribers();
  }, [searchTerm, activeOnly]);

  const loadSubscribers = async (page = 1) => {
    try {
      setLoading(true);
      const params = {
        page,
        per_page: 50,
        ...(searchTerm && { search: searchTerm }),
        ...(activeOnly && { active_only: true }),
      };
      const response = await adminSubscribersAPI.getAll(params);
      setSubscribers(response.data);
      if (response.pagination) {
        setPagination(response.pagination);
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "No se pudieron cargar los suscriptores",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <Toaster />
      <div>
        <h2 className="text-2xl font-bold text-white">Suscriptores</h2>
        <p className="text-[#A2A2A2]">Vista de suscriptores (solo lectura)</p>
      </div>

      <div className="flex flex-col sm:flex-row gap-4">
        <div className="relative flex-1">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-[#A2A2A2] w-5 h-5" />
        <Input
          type="text"
          placeholder="Buscar por nombre o email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 bg-white/5 border-white/10 text-white"
        />
        </div>
        <div className="flex items-center gap-2 px-4 py-2 bg-white/5 border border-white/10 rounded-lg">
          <Checkbox
            id="active-only"
            checked={activeOnly}
            onCheckedChange={(checked) => setActiveOnly(checked)}
            className="border-white/20"
          />
          <label
            htmlFor="active-only"
            className="text-sm text-white cursor-pointer select-none"
          >
            Solo activos
          </label>
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12 text-[#A2A2A2]">Cargando...</div>
      ) : subscribers.length === 0 ? (
        <div className="text-center py-12 text-[#A2A2A2]">No hay suscriptores registrados</div>
      ) : (
        <div className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
            <thead className="bg-white/5">
              <tr>
                <th className="px-6 py-4 text-left text-sm font-medium text-white">Nombre</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-white">Email</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-white">Teléfono</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-white">Estado suscripción</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-white">Fecha de alta</th>
                <th className="px-6 py-4 text-left text-sm font-medium text-white">Hora de suscripción</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/10">
              {subscribers.map((subscriber) => (
                <tr key={subscriber.id} className="hover:bg-white/5">
                  <td className="px-6 py-4 text-white font-medium">{subscriber.name}</td>
                  <td className="px-6 py-4 text-[#A2A2A2]">
                    <div className="flex items-center gap-2">
                      <Mail className="w-4 h-4" />
                      {subscriber.email}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-[#A2A2A2]">{subscriber.phone || '-'}</td>
                  <td className="px-6 py-4">
                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                      subscriber.subscription_status === 'active' || subscriber.subscription_status === 'trialing'
                        ? 'bg-green-500/20 text-green-400'
                        : 'bg-gray-500/20 text-gray-400'
                    }`}>
                      {subscriber.subscription_status || 'Sin suscripción'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-[#A2A2A2]">
                    <div className="flex items-center gap-2">
                      <Calendar className="w-4 h-4" />
                      {new Date(subscriber.created_at).toLocaleDateString()}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-[#A2A2A2]">
                    {subscriber.subscription_created_at ? (
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        {new Date(subscriber.subscription_created_at).toLocaleString('es-MX', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </div>
                    ) : (
                      <span className="text-[#666]">-</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {pagination.last_page > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-[#A2A2A2] text-sm">
            Mostrando {((pagination.current_page - 1) * pagination.per_page) + 1} a {Math.min(pagination.current_page * pagination.per_page, pagination.total)} de {pagination.total}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => loadSubscribers(pagination.current_page - 1)}
              disabled={pagination.current_page === 1}
              className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/10"
            >
              Anterior
            </button>
            <button
              onClick={() => loadSubscribers(pagination.current_page + 1)}
              disabled={pagination.current_page === pagination.last_page}
              className="px-4 py-2 bg-white/5 border border-white/10 rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed hover:bg-white/10"
            >
              Siguiente
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SubscribersManagement;

